import { Injectable } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { D2cApiService } from "./d2c-api.service";
import { D2cStorageService } from "./d2c-storage.service";
import { StageNavigationService } from "./stage-navigation.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { MatDialog } from "@angular/material/dialog";
import { MatDialogSizeService } from "src/app/shared/services/mat-dialog-size";
import { InfoDialogComponent } from "src/app/shared/dialogs/info-dialog/info-dialog.component";
import { GtmService } from "src/app/shared/services/gtm-service/gtm.service";
import { StoreService } from "src/app/shared/services/store.service";
import { LoanDashboardService } from "src/app/loan-layout/components/loan-dashboard/loan-dashboard.service";

@Injectable({ providedIn: "root" })
export class D2cResponseHandlerService {
    private stageRecoveryInProgress = false;

    constructor(
        private dialog: MatDialog,
        private api: D2cApiService,
        private storage: D2cStorageService,
        private stageNav: StageNavigationService,
        private loaderService: LoaderService,
        private toastr: ToastrService,
        private matDialogSizeService: MatDialogSizeService,
        private gtmService: GtmService,
        private storeService: StoreService,
        private loanDashboardService: LoanDashboardService
    ) {}

    /** 🔹 Normalize, store, navigate (optional) */
    handleResponse(res: any, skipNavigation = false): void {
        if (!res) return;

        const fromD2cDetails =
            (this.storeService.get("fromD2cDetails") as "REGISTER" | "DASHBOARD" | "CIBIL_CONFIRMATION") || "DASHBOARD";

        if (
            fromD2cDetails === "REGISTER" &&
            (res?.data?.stage === "NO_HISTORY" || res?.data?.stage === "NO_DATA_FOUND")
        ) {
            this.storage.set(res);
            this.stageNav.handle(res.data.stage);
            return;
        }

        let skipStorageCache = false;
        let errorDialog = true;
        const errorData = res?.error?.data || res?.data;

        // 1️⃣ Highest priority: DTC skip
        if (errorData?.isDtcSkip === true) {
            errorDialog = false;
        }
        if (res?.error?.messageSlug == "CIBIL_FULFILLMENT_REQUEST_FAILED" && errorDialog) {
            skipStorageCache = true;
            this.dialog
                .open(InfoDialogComponent, {
                    width: this.matDialogSizeService.wIII,
                    disableClose: true,
                    data: {
                        content: "CatchError",
                    },
                })
                .afterClosed()
                .subscribe((res: any) => {
                    if (res?.data) {
                        console.log("User chose to retry CIBIL fulfillment.");
                    }
                });
        }
        if (["SERVER_NOT_RESPONDING", "FAILED"].includes(res?.error?.data?.apiRequestStatus) && errorDialog) {
            console.log("CIBIL_FULFILLMENT_REQUEST_FAILED detected");
            skipStorageCache = true;
            let tryMinutes = res?.error.data.retryAfter;
            this.dialog
                .open(InfoDialogComponent, {
                    width: this.matDialogSizeService.wIII,
                    disableClose: true,
                    data: {
                        content: {
                            image: "assets/svg/icons/badge-info-gb.svg",
                            title: `We are facing some technical issues. Please try again after ${tryMinutes} minutes.`,
                            buttonName: "OK",
                        },
                    },
                })
                .afterClosed()
                .subscribe((res: any) => {
                    if (res?.data) {
                        console.log("User chose to retry CIBIL fulfillment.");
                    }
                });
        }
        const fullResponse = { ...res };
        console.log("Handling response:", fullResponse);

        const data = res?.data ?? res;
        const inner = data?.data ?? data;

        if (inner?.AnswerChoice) {
            fullResponse.questionKey = inner.Key || "";
            fullResponse.answerKey = inner.AnswerChoice?.AnswerChoiceId || "";
        } else if (data?.AnswerChoice) {
            fullResponse.questionKey = data.Key || "";
            fullResponse.answerKey = data.AnswerChoice?.AnswerChoiceId || "";
        }

        const prev = this.storage.get() || {};
        fullResponse.questionKey ||= prev.questionKey;
        fullResponse.answerKey ||= prev.answerKey;

        if (!skipStorageCache) this.storage.set(fullResponse);

        const stage = res?.data?.stage || fullResponse?.stage;
        if (!skipNavigation && stage) {
            this.stageNav.handle(stage);
        }
    }

    /** 🔁 Handle errors + recovery if backend says `callGetStatus` */
    handleError(err: any): void {
        const errorData = err?.error?.data || err?.data;

        if (errorData?.isDtcSkip) {
            this.loanDashboardService.getDashboardDetails(true);
            return;
        }

        if (errorData?.callGetStatus) {
            if (this.stageRecoveryInProgress) return;
            this.stageRecoveryInProgress = true;

            this.api.getStage().subscribe({
                next: (res) => {
                    const latest = res?.data ?? res;
                    const prev = this.storage.get() || {};
                    const next = { ...latest };

                    next.questionKey ||= prev.questionKey;
                    next.answerKey ||= prev.answerKey;

                    this.storage.set(next);

                    const stage = res?.stage || next?.stage;
                    if (stage) {
                        this.stageNav.handle(stage);
                    }
                },
                error: (e) => {
                    this.toastr.error("Failed to refresh stage. Please try again.");
                },
                complete: () => {
                    this.stageRecoveryInProgress = false;
                },
            });

            return;
        }

        const msg = err?.error?.message || err?.message || "Something went wrong. Please try again.";
        this.toastr.error(msg);
    }
}
